import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { api } from '../lib/api'
import { longDay, timeWindow } from '../lib/datetime'
import type { Booking } from '../lib/types'

/**
 * Confirmation, then the offer (01 §3.3 → §3.4).
 *
 * The offer is the *second* thing on this screen, never the first, and "No thanks" is a real
 * option rendered with equal weight. D1 puts the regular path first: someone who just wants a
 * visit booked has finished here.
 */
export function Confirmation() {
  const { id: estimateId, bookingId } = useParams<{ id: string; bookingId: string }>()

  const {
    data: booking,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => api.get<Booking>(`/bookings/${bookingId}`),
  })

  if (isError || (!isPending && !booking)) {
    // A dead link must not be an eternal spinner — the 2026-08-22 visual audit caught this
    // page "Loading…" for ever on a booking id that no longer existed.
    return (
      <main className="mx-auto max-w-2xl px-5 py-14">
        <h1 className="text-3xl">We could not find that visit</h1>
        <p className="mt-3 text-muted">
          The link may be old. Email{' '}
          <a href="mailto:andrew@asbhomerenovations.com" className="text-blue-deep underline">
            andrew@asbhomerenovations.com
          </a>{' '}
          and Andrew will sort it out.
        </p>
      </main>
    )
  }
  if (isPending || !booking) {
    return <p className="mx-auto max-w-2xl px-5 py-14 text-muted">Loading…</p>
  }

  // `booking.timezone` is the zone the visit is in, sent by the server. Formatting in the
  // browser's zone instead is what once showed the same 2:30 PM visit as 6:30 PM.
  const day = longDay(booking.slot_start, booking.timezone)
  const window = timeWindow(booking.slot_start, booking.slot_end, booking.timezone)

  return (
    <div className="mx-auto max-w-2xl px-5 py-12">
      <p className="eyebrow">Step 3 of 3 · Booked</p>
      <h1 className="mt-4 text-3xl">You are on Andrew’s calendar.</h1>

      <div className="mt-7 rounded-card bg-blue-tint p-6">
        <p className="m-0 font-mono text-xs font-semibold uppercase tracking-[0.18em] text-blue-deep">
          Your visit
        </p>
        <p className="m-0 mt-2 font-display text-2xl text-ink">{day}</p>
        <p className="m-0 mt-1 text-lg text-ink" data-testid="visit-time">
          {window}
        </p>
      </div>

      <h2 className="mt-9 text-xl">What happens next</h2>
      <p className="mt-2">
        Andrew comes out, measures, and talks through what you want. He confirms everything on site
        and gives you one fixed, all-in price in writing. A confirmation is in your inbox with a
        link to change or cancel.
      </p>

      <section className="mt-10 rounded-card border border-line bg-white p-6">
        <p className="eyebrow">Optional</p>
        <h2 className="mt-3 text-2xl">Want to see it in your room first?</h2>
        <p className="mt-3">
          Take a photo of the room and swap in real products from the stores we buy from.
          Andrew sees what you picked before he arrives. Five to fifteen minutes. Free.
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <Link
            to={`/estimate/${estimateId}/next`}
            className="rounded-btn bg-blue px-7 py-3.5 text-center font-bold text-white shadow-blue"
          >
            Open the design studio
          </Link>
          <Link
            to="/"
            className="rounded-btn border border-line-2 bg-white px-7 py-3.5 text-center font-semibold text-ink"
          >
            No thanks — I’m done
          </Link>
        </div>
      </section>
    </div>
  )
}
